import {
  emit,
  emittedOutput,
  isEmitted,
  type UpdateEffect,
  type EffectOutput,
  type Module,
  type Emitted,
  type ProcessMessage,
  type UpdateResult,
  type UpdateStep
} from './process'
import { Stream } from 'effect'
import type { Layer } from 'effect'
import type { RuntimeDefectReporter } from './runtime-defect-reporter'
import {
  scopeTask,
  taskProgramStream,
  type TaskDeclaration,
  type TaskProgram,
  type TaskScope
} from './task'

type UpdateTuple<State, Message extends ProcessMessage> = readonly [State, UpdateEffect<Message>]

type MessageFromEffectOutput<Output> = Output extends null
  ? never
  : Output extends ReadonlyArray<infer Item>
  ? Item extends ProcessMessage
    ? Item
    : never
  : Output extends ProcessMessage
  ? Output
  : never

function isUpdateTuple<State, Message extends ProcessMessage>(
  update: UpdateResult<State, Message>
): update is UpdateTuple<State, Message> {
  return Array.isArray(update)
}

function splitUpdate<State, Message extends ProcessMessage>(
  update: UpdateResult<State, Message>
): {
  state: State
  effect: UpdateEffect<Message> | undefined
} {
  if (!isUpdateTuple(update)) {
    return { state: update, effect: undefined }
  }
  return { state: update[0], effect: update[1] }
}

function combineEffects<Message extends ProcessMessage>(
  left: UpdateEffect<Message>,
  right: UpdateEffect<Message>
): UpdateEffect<Message> {
  // Commands from one composed transition start concurrently. Stream.merge
  // preserves Effect's fail-fast semantics: a failure interrupts unfinished
  // siblings, and their messages have no ordering guarantee.
  return Stream.merge(taskProgramStream(left), taskProgramStream(right))
}

/** Lifts child Command/Task output into the parent's message type. */
function mapMessage<From extends ProcessMessage, To extends ProcessMessage>(
  message: EffectOutput<From>,
  lift: (message: From) => To
): EffectOutput<To> {
  if (message == null) {
    return null
  }

  if (Array.isArray(message)) {
    return (message as readonly From[]).map(lift)
  }

  return lift(message as From)
}

function map<
  FromState,
  ToState,
  FromMessage extends ProcessMessage,
  ToMessage extends ProcessMessage
>(
  result: UpdateResult<FromState, FromMessage>,
  maps: {
    state: (state: FromState) => ToState
    message: (message: FromMessage) => ToMessage
  }
): UpdateResult<ToState, ToMessage> {
  if (!isUpdateTuple(result)) {
    return maps.state(result)
  }

  const [nextState, effect] = result
  return [
    maps.state(nextState),
    taskProgramStream(effect).pipe(Stream.map((message) => mapMessage(message, maps.message)))
  ] as const
}

function mapState<FromState, ToState, Message extends ProcessMessage>(
  result: UpdateResult<FromState, Message>,
  transform: (state: FromState) => ToState
): UpdateResult<ToState, Message> {
  return map(result, {
    state: transform,
    message: (message) => message
  })
}

function mergeUpdates<State, Message extends ProcessMessage>(
  base: UpdateResult<State, Message>,
  next: UpdateResult<State, Message>
): UpdateResult<State, Message> {
  const { effect: baseEffect } = splitUpdate(base)
  const { state: nextState, effect: nextEffect } = splitUpdate(next)

  if (baseEffect && nextEffect) {
    return [nextState, combineEffects(baseEffect, nextEffect)]
  }
  if (baseEffect) {
    return [nextState, baseEffect]
  }
  if (nextEffect) {
    return [nextState, nextEffect]
  }
  return nextState
}

type ScopedChildTask<
  Declaration,
  ParentMessage extends ProcessMessage
> = Declaration extends TaskDeclaration<any, infer Error, infer Requirements, infer Args>
  ? TaskDeclaration<ParentMessage, Error, Requirements, Args>
  : never

/**
 * One handler per declared output type. Each gets the output and the parent
 * state with the child's new state already written back.
 */
export type OutputHandlers<Output extends ProcessMessage, ParentState, Result> = {
  [Type in Output['type']]: (output: Extract<Output, { type: Type }>, state: ParentState) => Result
}

export type ChildHandle<
  ChildState,
  ChildEvent extends ProcessMessage,
  Name extends string,
  InitArg,
  Declaration extends TaskDeclaration<ChildEvent, never, any, any> = TaskDeclaration<ChildEvent>,
  Output extends ProcessMessage = never
> = {
  update: <ParentState, Result = UpdateResult<ParentState, never>>(
    state: ChildState,
    message: ChildEvent,
    toParent: (state: ChildState) => ParentState,
    ...outputs: [Output] extends [never]
      ? []
      : [OutputHandlers<Output, ParentState, NoInfer<Result>>]
  ) => Result | UpdateResult<ParentState, { type: Name; message: ChildEvent }>
  tasks: (
    state: ChildState
  ) => ReadonlyArray<ScopedChildTask<Declaration, { type: Name; message: ChildEvent }>>
  init: (arg: InitArg) => UpdateResult<ChildState, { type: Name; message: ChildEvent }>
  layer: Layer.Layer<never, any, RuntimeDefectReporter> | undefined
}

/**
 * Embeds one child module under a message name, in Elm's `updateWith` form.
 *
 * `update` reduces the child state it is given and hands the result to
 * `toParent`, which writes it back wherever the parent keeps it. Child Command
 * and Task messages are lifted through `{ type: name, message }`; Tasks also
 * get the name as their scope unless an explicit scope separates
 * interchangeable implementations. When the child emits an output, its
 * parent's handler runs in the same update, and the two transitions' Commands
 * merge. Children never address parents or siblings.
 */
export function child<
  ChildState,
  ChildEvent extends ProcessMessage,
  Name extends string,
  InitArg,
  Declaration extends TaskDeclaration<ChildEvent, never, any, any> = TaskDeclaration<ChildEvent>,
  Output extends ProcessMessage = never
>(
  spec: Module<ChildState, ChildEvent, InitArg, Declaration, Output>,
  name: Name,
  taskScope: TaskScope = [name]
): ChildHandle<ChildState, ChildEvent, Name, InitArg, Declaration, Output> {
  const lift = (message: ChildEvent) => ({ type: name, message })
  return {
    update: (state, message, toParent, ...outputs) => {
      const update: UpdateResult<ChildState, ChildEvent> | Emitted<ChildState, ChildEvent, Output> =
        spec.update(state, message)
      if (!isEmitted(update)) {
        return map(update, { state: toParent, message: lift })
      }
      const written = map(update.result, { state: toParent, message: lift })
      const value = emittedOutput(update)
      const handlers = outputs[0] as unknown as Record<
        string,
        (
          output: Output,
          state: unknown
        ) =>
          | UpdateResult<unknown, ProcessMessage>
          | Emitted<unknown, ProcessMessage, ProcessMessage>
      >
      const handled = handlers[value.type]!(value, splitUpdate(written).state)
      if (isEmitted(handled)) {
        return emit(mergeUpdates(written, handled.result), emittedOutput(handled)) as never
      }
      return mergeUpdates(written, handled) as never
    },
    tasks: (state) => {
      if (!spec.tasks) {
        return []
      }

      return spec
        .tasks(state)
        .map(
          (task) =>
            scopeTask(taskScope, task, lift) as ScopedChildTask<
              Declaration,
              { type: Name; message: ChildEvent }
            >
        )
    },
    init: (arg) =>
      map(spec.init(arg), {
        state: (nextState) => nextState,
        message: lift
      }),
    layer: spec.layer
  }
}

/**
 * Threads committed State through update steps from left to right.
 *
 * Commands are accumulated, not awaited: after the final state is known the
 * runtime merges them concurrently. A later step must therefore depend only on
 * the state returned by an earlier step, never on messages from its Command.
 */
function pipe<State, Message extends ProcessMessage = never>(
  initial: UpdateResult<State, Message>,
  ...steps: ReadonlyArray<UpdateStep<State, Message>>
): UpdateResult<State, Message> {
  let update: UpdateResult<any, any> = initial

  for (const step of steps) {
    const { state } = splitUpdate(update)
    update = mergeUpdates(update, step(state))
  }

  return update
}

type StateIndependentUpdateStep<Message extends ProcessMessage> = <State>(
  state: State
) => UpdateResult<State, Message>

function effect<Output>(
  run: () => TaskProgram<Output, never, unknown> | null | undefined
): StateIndependentUpdateStep<MessageFromEffectOutput<Output>>
function effect<State, Output>(
  run: (state: State) => TaskProgram<Output, never, unknown> | null | undefined
): UpdateStep<State, MessageFromEffectOutput<Output>>
function effect<State, Output>(
  run: (state: State) => TaskProgram<Output, never, unknown> | null | undefined
): UpdateStep<State, MessageFromEffectOutput<Output>> {
  return (state) => {
    const effect = run(state)
    if (effect == null) {
      return state
    }
    return [
      state,
      effect as TaskProgram<EffectOutput<MessageFromEffectOutput<Output>>, never, unknown>
    ]
  }
}

/**
 * Skips explicitly irrelevant messages before they enter an update function.
 * The predicate sees the same immutable state and message as the reducer; a
 * match returns the current state without running the reducer.
 */
function ignoreWhen<State, Message extends ProcessMessage>(
  shouldIgnore: (input: Readonly<{ state: State; message: Message }>) => boolean
): (
  update: (state: State, message: Message) => UpdateResult<State, Message>
) => (state: State, message: Message) => UpdateResult<State, Message> {
  return (update) => (state, message) => {
    if (shouldIgnore({ state, message })) {
      return state
    }

    return update(state, message)
  }
}

export const Update = {
  effect,
  ignoreWhen,
  map,
  mapState,
  pipe
}
